import { useI18n } from '../i18n'
import type { QRType, QRFormData } from '../lib/qrUtils'
import { cn } from '../lib/utils'

interface InputPanelProps {
  type: QRType
  data: QRFormData
  onChange: (data: QRFormData) => void
  errors: Record<string, string>
}

export function InputPanel({ type, data, onChange, errors }: InputPanelProps) {
  const { t } = useI18n()

  const update = (key: keyof QRFormData, value: string | boolean) => {
    onChange({ ...data, [key]: value })
  }

  const fieldClass = 'w-full px-3 py-2.5 bg-background border border-border rounded-lg text-foreground text-sm outline-none focus:ring-2 focus:ring-primary/30 placeholder:text-muted-foreground/50'
  const labelClass = 'block text-sm font-medium text-foreground mb-1.5'
  const errorClass = 'mt-1 text-xs text-destructive'

  return (
    <div className="space-y-4">
      {type === 'url' && (
        <div>
          <label className={labelClass}>
            {t('qr.input.urlLabel')}
            <span className="text-destructive ml-1">*</span>
          </label>
          <input
            type="text"
            value={data.url || ''}
            onChange={e => update('url', e.target.value)}
            placeholder={t('qr.input.urlPlaceholder')}
            className={fieldClass}
          />
          {errors.url && <p className={errorClass}>{errors.url}</p>}
        </div>
      )}

      {type === 'text' && (
        <div>
          <label className={labelClass}>
            {t('qr.input.textLabel')}
            <span className="text-destructive ml-1">*</span>
          </label>
          <textarea
            value={data.text || ''}
            onChange={e => update('text', e.target.value)}
            placeholder={t('qr.input.textPlaceholder')}
            rows={5}
            className={cn(fieldClass, 'resize-none')}
          />
          {errors.text && <p className={errorClass}>{errors.text}</p>}
        </div>
      )}

      {type === 'wifi' && (
        <>
          <div>
            <label className={labelClass}>
              {t('qr.input.wifi.ssidLabel')}
              <span className="text-destructive ml-1">*</span>
            </label>
            <input
              type="text"
              value={data.ssid || ''}
              onChange={e => update('ssid', e.target.value)}
              placeholder={t('qr.input.wifi.ssidPlaceholder')}
              className={fieldClass}
            />
            {errors.ssid && <p className={errorClass}>{errors.ssid}</p>}
          </div>
          <div>
            <label className={labelClass}>{t('qr.input.wifi.passwordLabel')}</label>
            <input
              type="text"
              value={data.password || ''}
              onChange={e => update('password', e.target.value)}
              placeholder={t('qr.input.wifi.passwordPlaceholder')}
              className={fieldClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>{t('qr.input.wifi.encryptionLabel')}</label>
              <select
                value={data.encryption || 'WPA'}
                onChange={e => update('encryption', e.target.value)}
                className={fieldClass}
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">None</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!data.hidden}
                  onChange={e => update('hidden', e.target.checked)}
                  className="w-4 h-4 rounded border-border accent-primary"
                />
                <span className="text-sm text-foreground">{t('qr.input.wifi.hiddenLabel')}</span>
              </label>
            </div>
          </div>
        </>
      )}

      {type === 'email' && (
        <>
          <div>
            <label className={labelClass}>
              {t('qr.input.email.emailLabel')}
              <span className="text-destructive ml-1">*</span>
            </label>
            <input
              type="text"
              value={data.email || ''}
              onChange={e => update('email', e.target.value)}
              placeholder={t('qr.input.email.emailPlaceholder')}
              className={fieldClass}
            />
            {errors.email && <p className={errorClass}>{errors.email}</p>}
          </div>
          <div>
            <label className={labelClass}>{t('qr.input.email.subjectLabel')}</label>
            <input
              type="text"
              value={data.subject || ''}
              onChange={e => update('subject', e.target.value)}
              placeholder={t('qr.input.email.subjectPlaceholder')}
              className={fieldClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('qr.input.email.bodyLabel')}</label>
            <textarea
              value={data.body || ''}
              onChange={e => update('body', e.target.value)}
              placeholder={t('qr.input.email.bodyPlaceholder')}
              rows={3}
              className={cn(fieldClass, 'resize-none')}
            />
          </div>
        </>
      )}

      {type === 'phone' && (
        <div>
          <label className={labelClass}>
            {t('qr.input.phoneLabel')}
            <span className="text-destructive ml-1">*</span>
          </label>
          <input
            type="text"
            value={data.phone || ''}
            onChange={e => update('phone', e.target.value)}
            placeholder={t('qr.input.phonePlaceholder')}
            className={fieldClass}
          />
          {errors.phone && <p className={errorClass}>{errors.phone}</p>}
        </div>
      )}

      {type === 'sms' && (
        <>
          <div>
            <label className={labelClass}>
              {t('qr.input.sms.phoneLabel')}
              <span className="text-destructive ml-1">*</span>
            </label>
            <input
              type="text"
              value={data.phone || ''}
              onChange={e => update('phone', e.target.value)}
              placeholder={t('qr.input.sms.phonePlaceholder')}
              className={fieldClass}
            />
            {errors.phone && <p className={errorClass}>{errors.phone}</p>}
          </div>
          <div>
            <label className={labelClass}>{t('qr.input.sms.messageLabel')}</label>
            <textarea
              value={data.message || ''}
              onChange={e => update('message', e.target.value)}
              placeholder={t('qr.input.sms.messagePlaceholder')}
              rows={3}
              className={cn(fieldClass, 'resize-none')}
            />
          </div>
        </>
      )}

      {type === 'vcard' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className={labelClass}>
              {t('qr.input.vcard.nameLabel')}
              <span className="text-destructive ml-1">*</span>
            </label>
            <input
              type="text"
              value={data.name || ''}
              onChange={e => update('name', e.target.value)}
              placeholder={t('qr.input.vcard.namePlaceholder')}
              className={fieldClass}
            />
            {errors.name && <p className={errorClass}>{errors.name}</p>}
          </div>
          <div>
            <label className={labelClass}>{t('qr.input.vcard.phoneLabel')}</label>
            <input
              type="text"
              value={data.phone || ''}
              onChange={e => update('phone', e.target.value)}
              placeholder={t('qr.input.vcard.phonePlaceholder')}
              className={fieldClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('qr.input.vcard.emailLabel')}</label>
            <input
              type="text"
              value={data.email || ''}
              onChange={e => update('email', e.target.value)}
              placeholder={t('qr.input.vcard.emailPlaceholder')}
              className={fieldClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('qr.input.vcard.orgLabel')}</label>
            <input
              type="text"
              value={data.org || ''}
              onChange={e => update('org', e.target.value)}
              placeholder={t('qr.input.vcard.orgPlaceholder')}
              className={fieldClass}
            />
          </div>
          <div>
            <label className={labelClass}>{t('qr.input.vcard.titleLabel')}</label>
            <input
              type="text"
              value={data.title || ''}
              onChange={e => update('title', e.target.value)}
              placeholder={t('qr.input.vcard.titlePlaceholder')}
              className={fieldClass}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>{t('qr.input.vcard.urlLabel')}</label>
            <input
              type="text"
              value={data.url_vcard || ''}
              onChange={e => update('url_vcard', e.target.value)}
              placeholder={t('qr.input.vcard.urlPlaceholder')}
              className={fieldClass}
            />
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass}>{t('qr.input.vcard.addressLabel')}</label>
            <input
              type="text"
              value={data.address || ''}
              onChange={e => update('address', e.target.value)}
              placeholder={t('qr.input.vcard.addressPlaceholder')}
              className={fieldClass}
            />
          </div>
        </div>
      )}
    </div>
  )
}
